import { FileText, ShoppingCart, Package, BookOpen } from 'lucide-react'
import { Separator } from '@/components/ui/separator'
import { CATEGORICAL } from '@/lib/theme'
import ProTip from '../ProTip'

export default function SalesSection() {
  return (
    <>
      <p className="text-sm text-muted-foreground mb-3">
        When a deal moves past the verbal agreement stage, you need quotes, orders, and a product
        catalog to back it up. The Sales module handles the commercial side of your deals — from
        pricing to fulfillment.
      </p>

      <section id="quotes" aria-labelledby="quotes-heading">
        <div className="flex items-center gap-2 mb-2">
          <FileText className="h-4 w-4" aria-hidden="true" />
          <h3 id="quotes-heading" className="font-semibold">
            Quotes
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          A quote is a formal price proposal you send to a customer. Each quote is linked to an
          opportunity and can include multiple line items with quantities, unit prices, and
          discounts.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">To create a quote:</span>
        </p>
        <ol
          className="list-decimal list-inside space-y-1 text-sm text-muted-foreground ml-2"
          role="list"
        >
          <li>
            Go to <span className="font-medium">Sales &rarr; Quotes</span> and click{' '}
            <span className="font-medium">New Quote</span> (or create one from an opportunity's
            detail page)
          </li>
          <li>Select the related opportunity and account</li>
          <li>Add line items — choose products from your catalog and set quantities</li>
          <li>Apply any discounts and set the expiration date</li>
          <li>Save and track the quote status as it moves through the approval process</li>
        </ol>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Address autocomplete:</span> When entering billing address
          details, start typing and select from Google Maps suggestions — the city, state, postal
          code, and country fill in automatically.
        </p>
        <p className="text-sm text-muted-foreground mt-2">
          <span className="font-medium">Quote PDFs:</span> Generate professional PDF proposals from
          any quote. On the quote detail page, click <span className="font-medium">View PDF</span>{' '}
          to open it in a new browser tab, or <span className="font-medium">Download PDF</span> to
          save it to your device. The PDF includes your company information, billing address, line
          items with quantities and unit prices, discounts, and totals.
        </p>
      </section>
      <Separator />

      <section id="orders" aria-labelledby="orders-heading">
        <div className="flex items-center gap-2 mb-2">
          <ShoppingCart className="h-4 w-4" aria-hidden="true" />
          <h3 id="orders-heading" className="font-semibold">
            Orders
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Orders track fulfillment, shipping, and payment status. They are linked to accounts and
          opportunities so you can trace the full lifecycle from lead to revenue. Orders are
          automatically created when an opportunity is marked Closed Won, or you can create them
          manually.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          <span className="font-medium">Order statuses:</span> Use the visual status stepper on each
          order detail page to track progression: Draft → Pending → Approved → Processed (or
          Denied). Filter by status on the list page to see what is in flight and what has been
          delivered.
        </p>
        <p className="text-sm text-muted-foreground">
          The Orders list page supports resizable and sortable columns — drag column edges to resize
          and click headers to sort.
        </p>
      </section>
      <Separator />

      <section id="products" aria-labelledby="products-heading">
        <div className="flex items-center gap-2 mb-2">
          <Package className="h-4 w-4" aria-hidden="true" />
          <h3 id="products-heading" className="font-semibold">
            Products
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          Your product catalog is the single source of truth for what you sell. Each product has a
          name, description, category and availability status. When you add line items to a quote or
          opportunity, you pick from this catalog.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Prices are held separately, one per currency.</span> A
          product does not have a single price — it has a price list entry for each currency you
          sell it in. GRM shows you the price for the currency of the deal you are working on. If
          no price has been set in that currency, it shows nothing rather than guessing: GRM never
          converts a price for you, because the exchange rates it holds are for reporting, not for
          quoting.
        </p>
      </section>
      <Separator />

      <section id="pricebooks" aria-labelledby="pricebooks-heading">
        <div className="flex items-center gap-2 mb-2">
          <BookOpen className="h-4 w-4" aria-hidden="true" />
          <h3 id="pricebooks-heading" className="font-semibold">
            Price List
          </h3>
        </div>
        <p className="text-sm text-muted-foreground mb-2">
          The price list holds the official list price of every product, one entry per currency. It
          is what GRM compares your quoted price against, so a discount can be measured and
          reported.
        </p>
        <p className="text-sm text-muted-foreground mb-2">
          When you quote below the list price, GRM shows you how far below and records it on the
          line. Large concessions are flagged to the approvers — including when the reduction is
          made by lowering the unit price rather than entering a discount.
        </p>
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Admin access required to change prices.</span> Admins also
          have a <span className="font-medium">Verify prices</span> check that reports problems in
          the price list (invalid currencies, duplicates, prices that look copied between currencies
          rather than converted). It only fixes what is unambiguous and reports anything that would
          change a real price for a person to decide.
        </p>
      </section>
      <Separator />

      <ProTip>
        <span className="font-medium">From quote to close:</span> Create your quote early in the
        negotiation — even before the customer asks for one. Having a professional, itemized
        proposal ready signals confidence and keeps the deal moving forward. See{' '}
        <a
          href="#sales-workflows"
          className="underline underline-offset-4 hover:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded"
          style={{ color: CATEGORICAL.navy.solid }}
        >
          Sales Workflows
        </a>{' '}
        for the full lead-to-close process.
      </ProTip>
    </>
  )
}
